import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getJob, postQuestionResponse } from "../../api/Client";
import TreeGenerationStatus from "./TreeGenerationStatus";
import "./QuizPanel.css";

export interface QuizQuestion {
    id: string;
    question: string;
    answers: string[];
    index: number;
    answer?: string;
}

/**
 * Fills the Learn page's tree section (see .learn-page__scroll) while a job
 * is in the QUESTIONS stage — asks one personalization question at a time
 * instead of covering the screen with a modal.
 */
export default function QuizPanel() {
    const { jobId } = useParams<{ jobId: string }>();
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ["job", jobId],
        queryFn: () => getJob(jobId!),
        enabled: !!jobId,
    });

    const allQuestions: QuizQuestion[] = [...(data?.questions ?? [])].sort(
        (a, b) => a.index - b.index
    );

    const firstUnansweredStep = allQuestions.findIndex((q) => !q.answer || q.answer.trim() === "");

    const [step, setStep] = useState(0);

    useEffect(() => {
        if (firstUnansweredStep !== -1) setStep(firstUnansweredStep);
    }, [firstUnansweredStep]);

    const currentQuestion = allQuestions[step];

    const answerMutation = useMutation({
        mutationFn: ({ questionId, answer }: { questionId: string; answer: string }) =>
            postQuestionResponse(questionId, answer),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["job", jobId] });
        },
    });

    const handleAnswer = (answer: string) => {
        if (!currentQuestion) return;

        answerMutation.mutate({ questionId: currentQuestion.id, answer });

        const isLastQuestion = step === allQuestions.length - 1;
        if (!isLastQuestion) {
            setStep((s) => s + 1);
        }
    };

    // Not loaded yet, questions haven't been generated yet, or every
    // question is already answered (waiting on the next generation stage) —
    // all read the same as "still generating" from here.
    if (isLoading || firstUnansweredStep === -1) {
        return <TreeGenerationStatus status="QUESTIONS" />;
    }

    return (
        <div className="quiz-panel">
            <span className="eyebrow quiz-panel__progress">
                Question {step + 1} of {allQuestions.length}
            </span>

            <p className="quiz-panel__question">{currentQuestion.question}</p>

            <div className="quiz-panel__options">
                {currentQuestion.answers.map((answer, i) => (
                    <button
                        key={`${answer}-${i}`}
                        type="button"
                        className="quiz-panel__option"
                        disabled={answerMutation.isPending}
                        onClick={() => handleAnswer(answer)}
                    >
                        {answer}
                    </button>
                ))}
            </div>

            {answerMutation.isError && (
                <p className="quiz-panel__error" role="alert">
                    Couldn't save that answer — it'll be asked again. Try selecting once more.
                </p>
            )}
        </div>
    );
}
